/**
 * ChangelogModal Component
 *
 * Displays the changelog/release notes in a modal
 * Reads from CHANGELOG.md as the single source of truth
 */

import { CSSProperties, useEffect, useMemo } from 'react';
import { APP_VERSION } from '@/constants/app';
import { parseChangelog, isValidChangelog, ChangelogFeature } from '@/utils/changelogParser';
import changelogRaw from '../../../CHANGELOG.md?raw';

interface ChangelogModalProps {
  isVisible: boolean;
  onClose: () => void;
}

function ChangelogModal({ isVisible, onClose }: ChangelogModalProps) {
  // Parse changelog once on mount
  const changelog = useMemo(() => {
    try {
      return parseChangelog(changelogRaw);
    } catch (error) {
      console.error('Failed to parse changelog:', error);
      return { versions: [], futurePlans: [] };
    }
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  // Styles
  const overlayStyles: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
  };

  const modalStyles: CSSProperties = {
    backgroundColor: 'var(--nb-bg-primary)',
    borderRadius: 'var(--nb-radius-lg)',
    padding: '0',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '1px solid var(--nb-border-primary)',
  };

  const headerStyles: CSSProperties = {
    padding: '24px 32px',
    borderBottom: '1px solid var(--nb-border-primary)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-xl)',
    fontWeight: 700,
    margin: 0,
    color: 'var(--nb-text-primary)',
  };

  const versionStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-secondary)',
    fontWeight: 500,
  };

  const contentStyles: CSSProperties = {
    padding: '24px 32px',
    overflowY: 'auto',
    flex: 1,
    color: 'var(--nb-text-primary)',
    lineHeight: '1.7',
  };

  const versionBlockStyles: CSSProperties = {
    marginBottom: '40px',
  };

  const versionHeaderStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-lg)',
    fontWeight: 700,
    marginBottom: '16px',
    color: 'var(--nb-accent-primary)',
    paddingBottom: '8px',
    borderBottom: '2px solid var(--nb-accent-primary)',
  };

  const versionSeparatorStyles: CSSProperties = {
    height: '1px',
    backgroundColor: 'var(--nb-border-primary)',
    margin: '32px 0',
  };

  const sectionStyles: CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-md)',
    fontWeight: 700,
    marginBottom: '12px',
    color: 'var(--nb-accent-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const listStyles: CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const listItemStyles: CSSProperties = {
    marginBottom: '12px',
    paddingLeft: '24px',
    position: 'relative',
  };

  const bulletStyles: CSSProperties = {
    position: 'absolute',
    left: '0',
    top: '0',
    color: 'var(--nb-accent-primary)',
    fontWeight: 700,
  };

  const featureTitleStyles: CSSProperties = {
    fontWeight: 600,
    color: 'var(--nb-text-primary)',
  };

  const featureDescStyles: CSSProperties = {
    color: 'var(--nb-text-secondary)',
    fontSize: 'var(--nb-font-size-sm)',
    marginTop: '4px',
  };

  const subBulletListStyles: CSSProperties = {
    listStyle: 'none',
    padding: '4px 0 0 16px',
    margin: 0,
  };

  const subBulletItemStyles: CSSProperties = {
    marginBottom: '4px',
    paddingLeft: '16px',
    position: 'relative',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-secondary)',
  };

  const subBulletStyles: CSSProperties = {
    position: 'absolute',
    left: '0',
    top: '0',
    color: 'var(--nb-text-secondary)',
  };

  const closeButtonStyles: CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: 'var(--nb-text-secondary)',
    padding: '4px 8px',
    lineHeight: 1,
    transition: 'color 0.2s',
  };

  const errorStyles: CSSProperties = {
    padding: '20px',
    textAlign: 'center',
    color: 'var(--nb-text-secondary)',
  };

  // Helper function to render a feature item
  const renderFeature = (feature: ChangelogFeature, index: number) => (
    <li key={index} style={listItemStyles}>
      <span style={bulletStyles}>•</span>
      <div>
        {feature.title && <div style={featureTitleStyles}>{feature.title}</div>}
        {feature.description && <div style={featureDescStyles}>{feature.description}</div>}
        {feature.bullets.length > 0 && (
          <ul style={subBulletListStyles}>
            {feature.bullets.map((bullet, i) => (
              <li key={i} style={subBulletItemStyles}>
                <span style={subBulletStyles}>◦</span>
                {bullet}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );

  // Helper function to get section emoji
  const getSectionEmoji = (section: string): string => {
    switch (section) {
      case 'added':
        return '✨';
      case 'changed':
        return '🔄';
      case 'fixed':
        return '🐛';
      default:
        return '📝';
    }
  };

  // Helper function to get section title
  const getSectionTitle = (section: string): string => {
    return section.charAt(0).toUpperCase() + section.slice(1);
  };

  return (
    <div style={overlayStyles} onClick={onClose}>
      <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyles}>
          <div>
            <h2 style={titleStyles}>What's New</h2>
            <div style={versionStyles}>Version {APP_VERSION}</div>
          </div>
          <button
            style={closeButtonStyles}
            onClick={onClose}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--nb-text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--nb-text-secondary)')}
            title="Close (Esc)"
          >
            ×
          </button>
        </div>

        <div style={contentStyles}>
          {!isValidChangelog(changelog) ? (
            <div style={errorStyles}>
              <p>⚠️ Unable to load changelog</p>
              <p style={{ fontSize: 'var(--nb-font-size-sm)', marginTop: '8px' }}>
                The changelog file could not be parsed. Please check the format.
              </p>
            </div>
          ) : (
            <>
              {/* Render all versions (newest first) */}
              {changelog.versions.map((version, versionIndex) => (
                <div key={version.version}>
                  <div style={versionBlockStyles}>
                    {/* Version header */}
                    <h3 style={versionHeaderStyles}>
                      Version {version.version} - {version.date}
                    </h3>

                    {/* Added section */}
                    {version.sections.added.length > 0 && (
                      <div style={sectionStyles}>
                        <div style={sectionTitleStyles}>
                          <span>{getSectionEmoji('added')}</span>
                          <span>{getSectionTitle('added')}</span>
                        </div>
                        <ul style={listStyles}>
                          {version.sections.added.map((feature, i) => renderFeature(feature, i))}
                        </ul>
                      </div>
                    )}

                    {/* Changed section */}
                    {version.sections.changed.length > 0 && (
                      <div style={sectionStyles}>
                        <div style={sectionTitleStyles}>
                          <span>{getSectionEmoji('changed')}</span>
                          <span>{getSectionTitle('changed')}</span>
                        </div>
                        <ul style={listStyles}>
                          {version.sections.changed.map((feature, i) => renderFeature(feature, i))}
                        </ul>
                      </div>
                    )}

                    {/* Fixed section */}
                    {version.sections.fixed.length > 0 && (
                      <div style={sectionStyles}>
                        <div style={sectionTitleStyles}>
                          <span>{getSectionEmoji('fixed')}</span>
                          <span>{getSectionTitle('fixed')}</span>
                        </div>
                        <ul style={listStyles}>
                          {version.sections.fixed.map((feature, i) => renderFeature(feature, i))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Separator between versions (not after last one) */}
                  {versionIndex < changelog.versions.length - 1 && (
                    <div style={versionSeparatorStyles} />
                  )}
                </div>
              ))}

              {/* Future Plans section */}
              {changelog.futurePlans.length > 0 && (
                <>
                  <div style={versionSeparatorStyles} />
                  <div style={sectionStyles}>
                    <div style={sectionTitleStyles}>
                      <span>🚀</span>
                      <span>Coming Soon</span>
                    </div>
                    <ul style={listStyles}>
                      {changelog.futurePlans.map((plan, i) => (
                        <li key={i} style={listItemStyles}>
                          <span style={bulletStyles}>•</span>
                          <div style={featureDescStyles}>{plan}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChangelogModal;
